import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import { maskStyle } from 'utils';
import { CourseStepStatus } from 'types';
import { getStepData } from 'utils/steps';
import { useCourseContext } from 'context';

import { StepButtonProps } from './types';

export const StepButton = ({ item, onClick }: StepButtonProps) => {
  const [isHovered, setHovered] = useState(false);
  const { getStepStatus } = useCourseContext();
  const { t } = useTranslation(['dashboard']);

  const { id, color, resourceVersions } = item;
  const { primaryClassName, secondaryClassName } = useMemo(
    () => getStepData(color),
    [color]
  );

  const status = useMemo(() => getStepStatus(id), [getStepStatus, id]);

  const isResource = resourceVersions && resourceVersions.length > 0;

  const isActive = status === CourseStepStatus.Active;
  const isLocked = status === CourseStepStatus.Locked;
  const isCompleted = status === CourseStepStatus.Completed;

  const isClickable = isActive || (isResource && isCompleted);

  useEffect(() => {
    if (!isClickable && isHovered) {
      setHovered(false);
    }
  }, [isClickable, isHovered]);

  const handleClick = isClickable ? onClick : undefined;
  const handleMouseEnter = isClickable ? () => setHovered(true) : undefined;
  const handleMouseLeave = isClickable ? () => setHovered(false) : undefined;

  const lockIconUrl = isLocked ? '/images/lock-on.svg' : '/images/lock-off.svg';

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={twMerge(
        'relative flex h-6 w-6 items-center justify-center rounded-full md:h-9 md:w-9',
        isLocked && 'opacity-40',
        isClickable &&
          'hover:bg-accent-gradient-secondary-hover active:bg-accent-gradient-secondary-active cursor-pointer',
        isActive
          ? `${primaryClassName} hover:bg-accent-gradient-primary-hover active:bg-accent-gradient-primary-active`
          : secondaryClassName
      )}
    >
      <div
        role="img"
        style={maskStyle(lockIconUrl)}
        aria-label={t(isLocked ? 'statusLock' : 'statusUnlocked')}
        className={twMerge(
          'h-3 w-3 md:h-5 md:w-5',
          isActive ? 'bg-white-primary' : primaryClassName
        )}
      />
    </div>
  );
};
